import {createContext, useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {useQueryClient} from '@tanstack/react-query';
import {API_URL} from '../utils/const/env';
import {
    AUTH_EXPIRED_EVENT,
    SECURITY_EXIT_EVENT,
    clearStoredAdminSession,
} from '../shared/api/client';

const AuthContext = createContext({});

axios.defaults.baseURL = API_URL;
if (localStorage && localStorage.sorpaToken) {
    const access_token = localStorage.getItem('sorpaToken');
    axios.defaults.headers.common['Authorization'] = `Token ${access_token}`;
}
axios.defaults.headers.post['Content-Type'] = 'application/json';

export const AuthProvider = ({children}) => {
    const queryClient = useQueryClient();
    const [auth, setAuth] = useState(() => localStorage.getItem('sorpaToken'));
    const [role, setRoleState] = useState(() =>
        localStorage.getItem('sorpaToken') ? localStorage.getItem('role') : null,
    );
    const [sessionVersion, setSessionVersion] = useState(0);
    const [me, setMe] = useState({});
    const [filterCities, setFilterCities] = useState([]);
    const [filterCategories, setFilterCategories] = useState([]);
    const authRef = useRef(auth);

    const clearRuntimeData = useCallback(() => {
        void queryClient.cancelQueries();
        queryClient.clear();
        setMe({});
        setFilterCities([]);
        setFilterCategories([]);
    }, [queryClient]);

    const replaceSession = useCallback(
        (nextToken) => {
            window.dispatchEvent(new Event(SECURITY_EXIT_EVENT));
            clearRuntimeData();
            clearStoredAdminSession();
            const normalizedToken = typeof nextToken === 'string' && nextToken ? nextToken : null;
            if (normalizedToken) {
                localStorage.setItem('sorpaToken', normalizedToken);
                axios.defaults.headers.common.Authorization = `Token ${normalizedToken}`;
            }
            authRef.current = normalizedToken;
            setAuth(normalizedToken);
            setRoleState(null);
            setSessionVersion((value) => value + 1);
        },
        [clearRuntimeData],
    );

    const startSession = useCallback((token) => replaceSession(token), [replaceSession]);
    const logout = useCallback(() => replaceSession(null), [replaceSession]);
    const setRole = useCallback((nextRole) => {
        const normalizedRole = typeof nextRole === 'string' && nextRole ? nextRole : null;
        if (normalizedRole && authRef.current) {
            localStorage.setItem('role', normalizedRole);
        } else {
            localStorage.removeItem('role');
        }
        setRoleState(normalizedRole);
    }, []);

    useEffect(() => {
        const syncAuth = () => {
            const nextToken = localStorage.getItem('sorpaToken');
            if (nextToken !== authRef.current) replaceSession(nextToken);
        };
        const expireSession = () => replaceSession(null);
        window.addEventListener('storage', syncAuth);
        window.addEventListener(AUTH_EXPIRED_EVENT, expireSession);
        return () => {
            window.removeEventListener('storage', syncAuth);
            window.removeEventListener(AUTH_EXPIRED_EVENT, expireSession);
        };
    }, [replaceSession]);

    return (
        <AuthContext.Provider
            value={{
                me,
                setMe,
                auth,
                role,
                setRole,
                setAuth: startSession,
                startSession,
                logout,
                sessionVersion,
                filterCities,
                setFilterCities,
                filterCategories,
                setFilterCategories,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
